// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ActionBar from '../layout/ActionBar';
import RequestKeys from './slideout/RequestKeys';
import RequestsMadeByMeGroupedList from './components/RequestsMadeByMeGroupedList';
import RequestsMadeByMeList from './components/RequestsMadeByMeList';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { userIs } from '../auth/authUtils';
import userTypes from '../../constants/userTypes';
import SlidingPane from 'react-sliding-pane';
import { toast } from 'react-toastify';
import TableViewSwitcher from './components/TableViewSwitcher';
import Button from '../common/Button';
import { useTranslation, Trans } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { downloadKeys } from '../../api/requestApi';
import { useAppSelector } from '../../hooks';

export interface RequestsMadeByMeProps {
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
}

function RequestsMadeByMe({ setCompleteRequestSlideoutOpen }: RequestsMadeByMeProps) {
  usePageTitle(['Made by me', 'Requests']);
  const currentUser = useAppSelector((state) => state.auth);
  const basket = useAppSelector((state) => state.basket);
  const { t } = useTranslation();

  const [reload, setReload] = useState<Date | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [requestKeysSlideoutOpen, setRequestKeysSlideoutOpen] = useState(false);
  const [requestsGrouped, setRequestsGrouped] = useLocalStorage(
    'requestsGrouped-made-by-me',
    false
  );
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const groupedParam = searchParams.get('grouped');
    if (groupedParam === 'true') {
      setRequestsGrouped(true);
    } else if (groupedParam === 'false') {
      setRequestsGrouped(false);
    }
  }, [searchParams, setRequestsGrouped]);

  useEffect(() => {
    if (searchParams.get('grouped') === null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('grouped', String(requestsGrouped));
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, requestsGrouped, setSearchParams]);

  const handleSetRequestsGrouped = (value: boolean) => {
    setRequestsGrouped(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('grouped', String(value));
        return next;
      },
      { replace: true }
    );
  };

  const [breadcrumb, setBreadcrumb] = useState([
    { label: t('Requests'), link: '/requests/made-by-me' },
  ]);

  const handleDownloadKeys = (e: React.MouseEvent, request: { id: string }) => {
    (e as any).cancelBubble = true;
    setIsProcessing(true);
    downloadKeys(request.id)
      .then(() => setIsProcessing(false))
      .catch((err) => {
        toast.error(err.message || t('Failed to download keys'));
        setIsProcessing(false);
      });
  };

  const [itemsInBasket, setItemsInBasket] = useState<number | undefined>();
  useEffect(() => {
    if (basket) {
      if (itemsInBasket !== undefined && itemsInBasket > basket.basketCount) {
        setReload(new Date());
      }
      setItemsInBasket(basket.basketCount);
    }
  }, [basket, itemsInBasket]);

  return (
    <>
      {basket && basket.basketCount > 0 && (
        <div
          className="alert alert-basket twinkle flex-shrink-0"
          onClick={() => setCompleteRequestSlideoutOpen(true)}
        >
          {basket.basketCount > 1 ? (
            <Trans
              i18nKey="basketCount"
              components={{ bold: <b /> }}
              values={{ basketCount: basket.basketCount }}
            >
              You have <bold>{basket.basketCount}</bold> requests to complete in
              your basket.
            </Trans>
          ) : (
            <Trans i18nKey="oneRequestToComplete" components={{ bold: <b /> }}>
              You have <bold>1</bold> request to complete in your basket.
            </Trans>
          )}
        </div>
      )}
      {basket && basket.submittingCount > 0 && (
        <div className="alert alert-basket twinkle flex-shrink-0">
          {basket.submittingCount > 1 ? (
            <Trans
              i18nKey="submittingCount"
              components={{ bold: <b /> }}
              values={{ submittingCount: basket.submittingCount }}
            >
              You have <bold>{basket.submittingCount}</bold> requests being
              processed.
            </Trans>
          ) : (
            <Trans
              i18nKey="oneRequestBeingProcessed"
              components={{ bold: <b /> }}
            >
              You have <bold>1</bold> request being processed.
            </Trans>
          )}
        </div>
      )}

      <ActionBar>
        {userIs(currentUser, userTypes.REQUESTER) && (
          <Button
            className="btn btn-primary"
            onClick={() => setRequestKeysSlideoutOpen(true)}
            text={t('Request keys')}
          />
        )}
      </ActionBar>
      {requestsGrouped ? (
        <RequestsMadeByMeGroupedList
          reload={reload}
          setBreadcrumb={setBreadcrumb}
          tableViewSwitcher={
            <TableViewSwitcher
              requestsGrouped={requestsGrouped}
              setRequestsGrouped={handleSetRequestsGrouped}
            />
          }
        />
      ) : (
        <RequestsMadeByMeList
          reload={reload}
          setBreadcrumb={setBreadcrumb}
          handleDownloadKeys={handleDownloadKeys}
          isProcessing={isProcessing}
          tableViewSwitcher={
            <TableViewSwitcher
              requestsGrouped={requestsGrouped}
              setRequestsGrouped={handleSetRequestsGrouped}
            />
          }
        />
      )}

      <SlidingPane
        isOpen={requestKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setRequestKeysSlideoutOpen(false)}
      >
        <RequestKeys
          done={() => {
            setRequestKeysSlideoutOpen(false);
            setCompleteRequestSlideoutOpen(true);
            setReload(new Date());
          }}
          cancel={() => setRequestKeysSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  );
}

export default RequestsMadeByMe;
